import { ThemeButton, ThemeTabBar } from './Theme';
import React, { useState, useEffect } from "react";
import { Loader2, Target } from "lucide-react";
import {
  tradingApi,
  StrategyStatus as StrategyStatusType,
} from "../services/api";
import LoadingSpinner from "./LoadingSpinner";
import ErrorDisplay from "./ErrorDisplay";

// Sub-components
import ONStrategyContent from "./StrategyStatusParts/ONStrategyContent";
import ORBStrategyContent from "./StrategyStatusParts/ORBStrategyContent";
import DTStrategyContent from "./StrategyStatusParts/DTStrategyContent";
import NQMMStrategyContent from "./StrategyStatusParts/NQMMStrategyContent";
import HydrationStatus from "./StrategyStatusParts/HydrationStatus";
import StrategyMarginStatus from "./StrategyMarginStatus";
import { useStrategyStatusQuery } from '../store/tradingApiSlice';
import { getStrategyDescription } from '../utils/strategyDescriptions';

type StrategyKey = "ON" | "ORB" | "DT" | "NQMM" | "MORB";
type StrategyToggleAction = "starting" | "pausing";
type StrategyToggleState = Partial<Record<StrategyKey, StrategyToggleAction>>;

interface StrategyStatusProps {
  className?: string;
}

const StrategyStatus: React.FC<StrategyStatusProps> = ({ className = '' }) => {
  const [activeTab, setActiveTab] = useState<StrategyKey>("ORB");
  const pollingOptions = (strategy: StrategyKey) => ({
    pollingInterval: activeTab === strategy ? 10000 : 60000,
    skipPollingIfUnfocused: true,
  });
  const onQuery = useStrategyStatusQuery('ON', pollingOptions('ON'));
  const orbQuery = useStrategyStatusQuery('ORB', pollingOptions('ORB'));
  const dtQuery = useStrategyStatusQuery('DT', pollingOptions('DT'));
  const nqmmQuery = useStrategyStatusQuery('NQMM', pollingOptions('NQMM'));
  const morbQuery = useStrategyStatusQuery('MORB', pollingOptions('MORB'));
  const [onStatus, setOnStatus] = useState<StrategyStatusType | null>(null);
  const [orbStatusState, setOrbStatusState] =
    useState<StrategyStatusType | null>(null);
  const [dtStatus, setDtStatus] = useState<StrategyStatusType | null>(null);
  const [nqmmStatus, setNqmmStatus] = useState<StrategyStatusType | null>(null);
  const [nqmmRefreshLoading, setNqmmRefreshLoading] = useState(false);
  const [morbStatus, setMorbStatus] = useState<StrategyStatusType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [strategyToggleLoading, setStrategyToggleLoading] =
    useState<StrategyToggleState>({});
  const pendingStrategyTargets = React.useRef<Partial<Record<StrategyKey, boolean>>>({});
  const strategyToggleLoadingRef = React.useRef<StrategyToggleState>({});
  const loading = onQuery.isLoading || orbQuery.isLoading || dtQuery.isLoading || nqmmQuery.isLoading || morbQuery.isLoading;
  const queryError = onQuery.error || orbQuery.error || dtQuery.error || nqmmQuery.error || morbQuery.error;

  const applyPendingTarget = React.useCallback((
    strategyName: StrategyKey,
    status: StrategyStatusType
  ): StrategyStatusType => {
    const pendingEnabled = pendingStrategyTargets.current[strategyName];
    if (pendingEnabled === undefined) return status;

    return { ...status, data: { ...status.data, enabled: pendingEnabled } };
  }, []);

  const setToggleLoadingForStrategy = React.useCallback((
    strategyName: StrategyKey,
    action: StrategyToggleAction | null
  ) => {
    strategyToggleLoadingRef.current = { ...strategyToggleLoadingRef.current };
    if (action) {
      strategyToggleLoadingRef.current[strategyName] = action;
    } else {
      delete strategyToggleLoadingRef.current[strategyName];
    }
    setStrategyToggleLoading(strategyToggleLoadingRef.current);
  }, []);

  const fetchData = React.useCallback(async () => {
    setError(null);
    try {
      const [onStatusData, orbStatusData, dtStatusData, nqmmStatusData, morbStatusData] = await Promise.all([
        onQuery.refetch().unwrap(),
        orbQuery.refetch().unwrap(),
        dtQuery.refetch().unwrap(),
        nqmmQuery.refetch().unwrap(),
        morbQuery.refetch().unwrap(),
      ]);

      setOnStatus(applyPendingTarget("ON", onStatusData));
      setOrbStatusState(applyPendingTarget("ORB", orbStatusData));
      setDtStatus(applyPendingTarget("DT", dtStatusData));
      setNqmmStatus(applyPendingTarget("NQMM", nqmmStatusData));
      setMorbStatus(applyPendingTarget("MORB", morbStatusData));

    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch strategy data"
      );
    }
  }, [applyPendingTarget, dtQuery, morbQuery, nqmmQuery, onQuery, orbQuery]);

  const refreshNQMMStatus = React.useCallback(async () => {
    setNqmmRefreshLoading(true);
    setError(null);
    try {
      const status = await nqmmQuery.refetch().unwrap();
      setNqmmStatus(applyPendingTarget("NQMM", status));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh Nasdaq momentum");
    } finally {
      setNqmmRefreshLoading(false);
    }
  }, [applyPendingTarget, nqmmQuery]);

  const updateStrategyEnabled = React.useCallback((
    strategyName: StrategyKey,
    enabled: boolean | undefined
  ) => {
    const updater = (prev: StrategyStatusType | null) =>
      prev
        ? { ...prev, data: { ...prev.data, enabled: Boolean(enabled) } }
        : prev;

    if (strategyName === "ORB") {
      setOrbStatusState(updater);
    } else if (strategyName === "DT") {
      setDtStatus(updater);
    } else if (strategyName === "NQMM") {
      setNqmmStatus(updater);
    } else if (strategyName === "MORB") {
      setMorbStatus(updater);
    } else {
      setOnStatus(updater);
    }
  }, []);

  const handleStrategyToggle = React.useCallback(async (
    strategyName: StrategyKey,
    currentlyEnabled: boolean | undefined
  ) => {
    if (strategyToggleLoadingRef.current[strategyName]) return;

    const newState = !currentlyEnabled;
    pendingStrategyTargets.current[strategyName] = newState;
    setToggleLoadingForStrategy(strategyName, newState ? "starting" : "pausing");

    updateStrategyEnabled(strategyName, newState);

    try {
      const response = await tradingApi.setStrategyEnabled(strategyName, newState);

      if (!response.success) {
        throw new Error(response.message || `Failed to update ${strategyName} strategy state`);
      }

      await fetchData();
      updateStrategyEnabled(strategyName, newState);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to update ${strategyName} strategy state`
      );
      delete pendingStrategyTargets.current[strategyName];
      updateStrategyEnabled(strategyName, currentlyEnabled);
    } finally {
      delete pendingStrategyTargets.current[strategyName];
      setToggleLoadingForStrategy(strategyName, null);
    }
  }, [fetchData, setToggleLoadingForStrategy, updateStrategyEnabled]);

  const strategySummaries = React.useMemo(
    () => [
      { name: "ORB" as const, enabled: orbStatusState?.data?.enabled },
      { name: "DT" as const, enabled: dtStatus?.data?.enabled },
      { name: "ON" as const, enabled: onStatus?.data?.enabled },
      { name: "NQMM" as const, enabled: nqmmStatus?.data?.enabled },
      { name: "MORB" as const, enabled: morbStatus?.data?.enabled },
    ],
    [dtStatus?.data?.enabled, morbStatus?.data?.enabled, nqmmStatus?.data?.enabled, onStatus?.data?.enabled, orbStatusState?.data?.enabled]
  );

  const runningCount = strategySummaries.filter((strategy) => strategy.enabled).length;
  const pausedCount = strategySummaries.length - runningCount;
  const activeStatus = activeTab === "ON"
    ? onStatus?.data
    : activeTab === "DT"
    ? dtStatus?.data
      : activeTab === "NQMM"
        ? nqmmStatus?.data
        : activeTab === "MORB"
          ? morbStatus?.data
          : orbStatusState?.data;

  useEffect(() => {
    if (onQuery.data) setOnStatus(applyPendingTarget('ON', onQuery.data));
    if (orbQuery.data) setOrbStatusState(applyPendingTarget('ORB', orbQuery.data));
    if (dtQuery.data) setDtStatus(applyPendingTarget('DT', dtQuery.data));
    if (nqmmQuery.data) setNqmmStatus(applyPendingTarget('NQMM', nqmmQuery.data));
    if (morbQuery.data) setMorbStatus(applyPendingTarget('MORB', morbQuery.data));
  }, [applyPendingTarget, dtQuery.data, morbQuery.data, nqmmQuery.data, onQuery.data, orbQuery.data]);

  useEffect(() => {
    const handleRefresh = () => fetchData();
    window.addEventListener('trading-data-refresh', handleRefresh);
    return () => window.removeEventListener('trading-data-refresh', handleRefresh);
  }, [fetchData]);

  return (
    <div className={`panel full-bleed-panel flex min-h-0 w-full flex-col ${className}`}>
      <div className="flex shrink-0 flex-col gap-4 border-b border-white/10 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-neon/10 text-neon">
            <Target className="w-5 h-5" />
          </div>
          <div className="space-y-0.5">
            <h2 className="text-lg font-bold text-white tracking-tight">Strategy Oversight</h2>
            <p className="text-xs text-muted">Real-time signal analysis and lifecycle control</p>
          </div>
        </div>
      </div>

      <ThemeTabBar aria-label="Strategy sections">
        {(["ORB", "DT", "ON", "NQMM", "MORB"] as const).map((tab) => (
          <ThemeButton
            key={tab}
            aria-pressed={activeTab === tab}
            variant={activeTab === tab ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </ThemeButton>
        ))}
      </ThemeTabBar>

      <div className="flex min-h-0 flex-1 flex-col p-5">
        {loading &&
          !(onStatus && onStatus.data) &&
          !(orbStatusState && orbStatusState.data) &&
          !(dtStatus && dtStatus.data) &&
          !(nqmmStatus && nqmmStatus.data) &&
          !(morbStatus && morbStatus.data) && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          )}

        {(error || (queryError && !(onStatus || orbStatusState || dtStatus || nqmmStatus || morbStatus))) && (
          <ErrorDisplay
            error={error || 'Failed to fetch strategy data'}
            onRetry={() => fetchData()}
            message="Failed to load strategy data"
          />
        )}

        {(onStatus || orbStatusState || dtStatus || nqmmStatus || morbStatus) ? (
          <div className="space-y-4 text-foreground">
            {/* Strategy overview chips & Hydration Status */}
            <div className="flex flex-wrap items-center gap-4 mb-1">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-success-500/10 border border-success-500/20 text-success-400">
                  <span className="text-[10px] uppercase font-black tracking-wider">Running</span>
                  <span className="min-w-5 h-5 px-1 rounded-full bg-success-500/20 text-center text-xs font-black leading-5">
                    {runningCount}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/60">
                  <span className="text-[10px] uppercase font-black tracking-wider">Paused</span>
                  <span className="min-w-5 h-5 px-1 rounded-full bg-white/10 text-center text-xs font-black leading-5">
                    {pausedCount}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5">
                  <span
                    className={`h-2 w-2 rounded-full ${strategyToggleLoading.ORB ? "bg-warning-500 animate-pulse" : orbStatusState?.data?.enabled ? "bg-success-500 animate-pulse" : "bg-white/30"
                      }`}
                  />
                  <span className="uppercase text-xs font-bold text-white/70">ORB</span>
                  {strategyToggleLoading.ORB && <Loader2 className="h-3 w-3 animate-spin text-warning-500" />}
                </div>
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5">
                  <span
                    className={`h-2 w-2 rounded-full ${strategyToggleLoading.DT ? "bg-warning-500 animate-pulse" : dtStatus?.data?.enabled ? "bg-success-500 animate-pulse" : "bg-white/30"
                      }`}
                  />
                  <span className="uppercase text-xs font-bold text-white/70">DT</span>
                  {strategyToggleLoading.DT && <Loader2 className="h-3 w-3 animate-spin text-warning-500" />}
                </div>
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5">
                  <span
                    className={`h-2 w-2 rounded-full ${strategyToggleLoading.ON ? "bg-warning-500 animate-pulse" : onStatus?.data?.enabled ? "bg-success-500 animate-pulse" : "bg-white/30"
                      }`}
                  />
                  <span className="uppercase text-xs font-bold text-white/70">ON</span>
                  {strategyToggleLoading.ON && <Loader2 className="h-3 w-3 animate-spin text-warning-500" />}
                </div>
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5">
                  <span
                    className={`h-2 w-2 rounded-full ${strategyToggleLoading.NQMM ? "bg-warning-500 animate-pulse" : nqmmStatus?.data?.enabled ? "bg-success-500 animate-pulse" : "bg-white/30"
                      }`}
                  />
                  <span className="uppercase text-xs font-bold text-white/70">NQMM</span>
                  {strategyToggleLoading.NQMM && <Loader2 className="h-3 w-3 animate-spin text-warning-500" />}
                </div>
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5">
                  <span
                    className={`h-2 w-2 rounded-full ${strategyToggleLoading.MORB ? "bg-warning-500 animate-pulse" : morbStatus?.data?.enabled ? "bg-success-500 animate-pulse" : "bg-white/30"
                      }`}
                  />
                  <span className="uppercase text-xs font-bold text-white/70">MORB</span>
                  {strategyToggleLoading.MORB && <Loader2 className="h-3 w-3 animate-spin text-warning-500" />}
                </div>
              </div>

              {activeStatus && (
                <div
                  className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                    activeStatus.runtime_stale
                      ? "border-warning-500/25 bg-warning-500/10 text-warning-500"
                      : "border-info-500/20 bg-info-500/10 text-info-500"
                  }`}
                  title={activeStatus.runtime_synced_at
                    ? `Trading engine snapshot: ${activeStatus.runtime_synced_at}`
                    : "Status is served by this trading engine process"}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${activeStatus.runtime_stale ? "bg-warning-500" : "bg-info-500 animate-pulse"}`} />
                  {activeStatus.runtime_stale ? "Engine sync delayed" : "Engine synced"}
                </div>
              )}

              {/* Hydration Status Bar */}
              <div className="flex items-center gap-2 ml-auto">
                <HydrationStatus
                  enabled={orbStatusState?.data?.enabled}
                  hydrated={orbStatusState?.data?.hydrated}
                  strategyName="ORB"
                />
                <HydrationStatus
                  enabled={dtStatus?.data?.enabled}
                  hydrated={dtStatus?.data?.hydrated}
                  strategyName="DT"
                />
                <HydrationStatus
                  enabled={onStatus?.data?.enabled}
                  hydrated={onStatus?.data?.hydrated}
                  strategyName="ON"
                />
                <HydrationStatus
                  enabled={nqmmStatus?.data?.enabled}
                  hydrated={nqmmStatus?.data?.hydrated}
                  strategyName="NQMM"
                />
                <HydrationStatus
                  enabled={morbStatus?.data?.enabled}
                  hydrated={morbStatus?.data?.hydrated}
                  strategyName="MORB"
                />
              </div>
            </div>

            {activeStatus && (
              <StrategyOverview
                strategy={activeTab}
                status={activeStatus}
              />
            )}

            {activeStatus && <StrategyMarginStatus status={activeStatus} />}

            {activeTab === "ORB" && (
              <ORBStrategyContent
                orbStatusState={orbStatusState}
                handleStrategyToggle={handleStrategyToggle}
                strategyToggleLoading={strategyToggleLoading}
              />
            )}
            {activeTab === "DT" && (
              <DTStrategyContent
                dtStatus={dtStatus}
                handleStrategyToggle={handleStrategyToggle}
                strategyToggleLoading={strategyToggleLoading}
              />
            )}
            {activeTab === "ON" && (
              <ONStrategyContent
                onStatus={onStatus}
                handleStrategyToggle={handleStrategyToggle}
                strategyToggleLoading={strategyToggleLoading}
              />
            )}
            {activeTab === "NQMM" && (
              <NQMMStrategyContent
                nqmmStatus={nqmmStatus}
                handleStrategyToggle={handleStrategyToggle}
                strategyToggleLoading={strategyToggleLoading}
                refreshNQMMStatus={refreshNQMMStatus}
                rankingRefreshLoading={nqmmRefreshLoading}
              />
            )}
            {activeTab === "MORB" && (
              <ManagedORBOverview
                status={morbStatus}
                handleStrategyToggle={handleStrategyToggle}
                strategyToggleLoading={strategyToggleLoading}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

interface StrategyOverviewProps {
  strategy: StrategyKey;
  status: StrategyStatusType['data'];
}

const StrategyOverview: React.FC<StrategyOverviewProps> = ({ strategy, status }) => {
  const description = getStrategyDescription(strategy);
  const symbols = status.active_symbols?.length
    ? status.active_symbols
    : status.managed_symbols || [];

  return (
    <section className="rounded-2xl border border-neon/15 bg-neon/[0.04] px-4 py-3.5 sm:px-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-neon">Strategy overview</span>
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/60">
              {description.label}
            </span>
          </div>
          <p className="mt-2 max-w-4xl text-sm leading-relaxed text-white/75">{description.overview}</p>
        </div>
        <div className="shrink-0 text-left text-[10px] text-white/45 sm:text-right">
          <div className="font-bold uppercase tracking-wider text-white/60">Tracked symbols</div>
          <div className="mt-1 max-w-[260px] sm:max-w-[320px]">
            {symbols.length ? symbols.join(', ') : 'None currently reported'}
          </div>
        </div>
      </div>
    </section>
  );
};

interface ManagedORBOverviewProps {
  status: StrategyStatusType | null;
  handleStrategyToggle: (strategyName: StrategyKey, currentlyEnabled: boolean | undefined) => Promise<void>;
  strategyToggleLoading: Partial<Record<StrategyKey, StrategyToggleAction>>;
}

const ManagedORBOverview: React.FC<ManagedORBOverviewProps> = ({
  status,
  handleStrategyToggle,
  strategyToggleLoading,
}) => {
  const enabled = status?.data?.enabled;
  const toggleState = strategyToggleLoading.MORB;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div>
        <h3 className="text-sm font-bold text-white">Managed ORB control</h3>
        <p className="mt-1 text-xs leading-relaxed text-muted">
          Configure selected symbols, entry quality gates, staged stops, and T+3 reductions from the Managed ORB page.
        </p>
      </div>
      <button
        type="button"
        onClick={() => void handleStrategyToggle('MORB', enabled)}
        disabled={Boolean(toggleState)}
        className={`inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl border px-4 text-[10px] font-black uppercase tracking-widest transition disabled:cursor-not-allowed disabled:opacity-50 ${enabled
          ? 'border-warning-500/30 bg-warning-500/10 text-warning-500 hover:bg-warning-500/20'
          : 'border-neon/30 bg-neon/10 text-neon hover:bg-neon/20'
          }`}
      >
        {toggleState && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {toggleState ? (toggleState === 'starting' ? 'Starting…' : 'Pausing…') : enabled ? 'Pause strategy' : 'Start strategy'}
      </button>
    </div>
  );
};

export default StrategyStatus;
